import type { GbaButton, MgbaCore } from "./types";
import layout from "./keymap.json";
import { onPreferencesApplied, setPref } from "@common/preferences";
import { onControllersChange, profileById, setProfileKeys, type ControllerProfile } from "./controllers";

// The keyboard layout, for every console and both apps, is keymap.json:
// KeyboardEvent.code names (which winit's KeyCode shares) against each
// console's buttons. The desktop game process reads the same file
// (desktop/emu/src/keymap.rs), so a key means the same thing everywhere.
// The player's own choices sit on top as overrides. Those are the Default
// profile's, kept in this browser (and the account); a game can play with
// a shared controller profile instead (controllers.ts), chosen per game.
// The app writes the game's overrides out for the game process at each
// launch. Each engine below turns the result into its own bindings.

export type Layout = keyof typeof layout.consoles;

/** The layout a console plays with: the Game Boys share one. */
export function layoutOf(system: string): Layout {
  return system in layout.consoles ? (system as Layout) : "gb";
}

export interface Binding {
  button: string;
  keys: string[];
}

/** A console's buttons with the keys the player chose; a whole console is absent when untouched. */
export type Overrides = Partial<Record<Layout, Record<string, string[]>>> & { turbo?: string };

const STORAGE_KEY = "pocket-play.keymap";
/** `<prefix><game>`: the profile id the game plays with, or "default"; absent follows its console. */
const GAME_PREFIX = "pocket-play.controller.";
/** `<prefix><console>`: the profile id a console's games play with; absent is Default. */
const CONSOLE_PREFIX = "pocket-play.consoleController.";
/** A game's choice of Default over its console's profile. */
export const DEFAULT_PROFILE = "default";
const listeners = new Set<() => void>();
let overrides: Overrides = read();
// The game running on this page, whose profile the engines' keys come from.
let playing: KeyTarget = null;
let version = 0;

function read(): Overrides {
  try {
    return (JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") as Overrides) ?? {};
  } catch {
    return {};
  }
}

function changed() {
  version++;
  for (const listener of listeners) listener();
}

// The account's layout and choices can replace this browser's at load,
// after this module has read them; the profiles list arrives after that.
onPreferencesApplied(() => {
  overrides = read();
  changed();
});
onControllersChange(changed);

/** Runs `listener` whenever a binding changes; returns the way to stop. */
export function onKeymapChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Changes whenever any binding does, for useSyncExternalStore. */
export const keymapVersion = (): number => version;

/**
 * Whose keys: a game's (its own profile, else its console's, else
 * Default's), or a profile's own, with null for Default (the controller
 * profiles page).
 */
export type KeyTarget = { game: string; console: Layout } | { profile: string | null } | null;

/** What a mapping to a profile covers: one game, or every game of a console. */
export type Scope = { game: string } | { console: Layout };

const keyOf = (scope: Scope) => ("game" in scope ? GAME_PREFIX + scope.game : CONSOLE_PREFIX + scope.console);

/** The game whose keys the engines take from now on. */
export function setKeymapGame(game: string, console: Layout) {
  if (playing && "game" in playing && playing.game === game && playing.console === console) return;
  playing = { game, console };
  changed();
}

/** The profile id mapped to a scope (or "default" for a game), if any. */
export function choiceOf(scope: Scope): string | null {
  try {
    return localStorage.getItem(keyOf(scope));
  } catch {
    return null;
  }
}

/** Maps a scope to a profile (or a game to DEFAULT_PROFILE); null takes the mapping away. */
export function chooseProfile(scope: Scope, id: string | null) {
  setPref(keyOf(scope), id);
  changed();
}

/** Every mapping the player has made, consoles first. */
export function mappings(): { scope: Scope; id: string }[] {
  const found: { scope: Scope; id: string }[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const id = key && localStorage.getItem(key);
      if (!key || !id) continue;
      if (key.startsWith(CONSOLE_PREFIX) && key.slice(CONSOLE_PREFIX.length) in layout.consoles) {
        found.push({ scope: { console: key.slice(CONSOLE_PREFIX.length) as Layout }, id });
      } else if (key.startsWith(GAME_PREFIX)) {
        found.push({ scope: { game: key.slice(GAME_PREFIX.length) }, id });
      }
    }
  } catch {
    // None readable.
  }
  const label = (s: Scope) => ("console" in s ? `0${s.console}` : `1${s.game}`);
  return found.sort((a, b) => label(a.scope).localeCompare(label(b.scope)));
}

/** Which mapping a game's keys come from: its own, its console's, or none (Default). */
export function mappedBy(target: { game: string; console: Layout }): "game" | "console" | null {
  const own = choiceOf({ game: target.game });
  if (own === DEFAULT_PROFILE) return "game";
  if (own && profileById(own)) return "game";
  const console = choiceOf({ console: target.console });
  return console && profileById(console) ? "console" : null;
}

/** The profile a target plays with; undefined is Default (as is one since deleted). */
export function profileOf(target: KeyTarget = playing): ControllerProfile | undefined {
  if (!target) return undefined;
  if ("profile" in target) return target.profile ? profileById(target.profile) : undefined;
  const by = mappedBy(target);
  if (by === "game") {
    const own = choiceOf({ game: target.game });
    return own === DEFAULT_PROFILE ? undefined : profileById(own!);
  }
  return by === "console" ? profileById(choiceOf({ console: target.console })!) : undefined;
}

/** The player's choices for a target, its profile's or Default's; for the desktop game process too. */
export const overridesFor = (game: KeyTarget = playing): Overrides => profileOf(game)?.keys ?? overrides;

/** Whether the target's keys can be changed: Default's always, a profile's by its maker. */
export const canEditKeys = (game: KeyTarget = playing): boolean => profileOf(game)?.editable ?? true;

function write(next: Overrides, game: KeyTarget) {
  const profile = profileOf(game);
  if (profile) {
    setProfileKeys(profile.id, next);
    return;
  }
  overrides = next;
  try {
    setPref(STORAGE_KEY, Object.keys(next).length ? JSON.stringify(next) : null);
  } catch {
    // Kept for this page at least.
  }
  changed();
}

function defaults(system: Layout): Binding[] {
  return [
    ...Object.entries(layout.directions).map(([button, keys]) => ({ button, keys })),
    ...layout.consoles[system].buttons,
  ];
}

/** Directions first, then the console's buttons, in the order they're shown, with the game's keys. */
export function bindings(system: Layout, game: KeyTarget = playing): Binding[] {
  const chosen = overridesFor(game)[system] ?? {};
  return defaults(system).map(({ button, keys }) => ({ button, keys: chosen[button] ?? keys }));
}

/** Each layout's consoles, as the controller pickers name them. */
export const LAYOUT_LABEL: Record<Layout, string> = {
  gb: "Game Boy, Color and Advance",
  n64: "N64",
  psx: "PlayStation",
  psp: "PSP",
};

export const LAYOUTS = Object.keys(layout.consoles) as Layout[];

/** What the directions move, for the help: "D-pad" or "analog stick". */
export const movesOf = (system: Layout): string => layout.consoles[system].moves;

const DEFAULT_TURBO = Object.entries(layout.hotkeys).find(([, action]) => action === "turbo")![0];

/** Held to fast-forward, in every engine. */
export const turboKey = (game: KeyTarget = playing): string => overridesFor(game).turbo ?? DEFAULT_TURBO;

/** Whether a console's keys, or the turbo key, differ from the shared layout. */
export const isCustomised = (system: Layout, game: KeyTarget = playing): boolean => {
  const chosen = overridesFor(game);
  return system in chosen || "turbo" in chosen;
};

/**
 * Puts `code` on slot `slot` of a button (or on turbo, with button null). A
 * key does one thing per console, so it leaves any other button it was on.
 */
export function assignKey(
  system: Layout,
  button: string | null,
  slot: number,
  code: string,
  game: KeyTarget = playing,
) {
  const next: Overrides = {
    ...overridesFor(game),
    [system]: Object.fromEntries(bindings(system, game).map((b) => [b.button, [...b.keys]])),
  };
  const console = next[system]!;
  for (const keys of Object.values(console)) {
    const at = keys.indexOf(code);
    if (at >= 0) keys.splice(at, 1);
  }
  if (button === null) next.turbo = code;
  else {
    if (turboKey(game) === code) next.turbo = "";
    const keys = console[button];
    if (slot < keys.length) keys[slot] = code;
    else keys.push(code);
  }
  // A button with no key left gets nothing; the player can see and fix it.
  write(next, game);
}

/** Back to the shared layout for one console (and the turbo key). */
export function resetKeys(system: Layout, game: KeyTarget = playing) {
  const next = { ...overridesFor(game) };
  delete next[system];
  delete next.turbo;
  write(next, game);
}

/** Set while the Keyboard panel waits for a key, so the game ignores it. */
export let capturing = false;
export const setCapturing = (on: boolean) => {
  capturing = on;
};

/** A key code as the help shows it: KeyX → X, ArrowUp → ↑, Slash → /. */
export function keyLabel(code: string): string {
  const named: Record<string, string> = {
    ArrowUp: "↑",
    ArrowDown: "↓",
    ArrowLeft: "←",
    ArrowRight: "→",
    Slash: "/",
    Period: ".",
    Comma: ",",
    Semicolon: ";",
    Quote: "'",
    BracketLeft: "[",
    BracketRight: "]",
    Backslash: "\\",
    Minus: "-",
    Equal: "=",
    Backquote: "`",
  };
  return named[code] ?? code.replace(/^(Key|Digit|Numpad)/, (p) => (p === "Numpad" ? "Num " : ""));
}

// mGBA takes bindings by SDL key name, one per button (bindKey replaces the
// previous one), so a button's first key goes through it and the rest
// through buttonPress below, which ORs into the same bitmask.
function sdlKeyName(code: string): string {
  const named: Record<string, string> = {
    Enter: "Return",
    Slash: "/",
    Period: ".",
    Comma: ",",
    Semicolon: ";",
    Quote: "'",
    Minus: "-",
    Equal: "=",
  };
  return (
    named[code] ??
    code
      .replace(/^Key/, "")
      .replace(/^Arrow/, "")
      .replace(/^Digit/, "")
  );
}

const gb = () => bindings("gb") as { button: GbaButton; keys: string[] }[];

/** Second and later keys for each Game Boy button, by key code. */
export const extraKeys = (): Record<string, GbaButton> =>
  Object.fromEntries(gb().flatMap(({ button, keys }) => keys.slice(1).map((code) => [code, button])));

// loadGame re-initializes the core's input map, so call this after every
// load, and again when a binding changes. A button with no key is bound to
// nothing SDL names.
export function applyKeymap(core: MgbaCore) {
  for (const { button, keys } of gb()) core.bindKey(keys.length ? sdlKeyName(keys[0]) : "Unknown", button);
}

// Keys the core's own SDL listener doesn't handle the way we want. It runs
// in the bubble phase on window, so a capture listener gets there first.
//
// Fast-forward: setFastForwardMultiplier is a persistent speed, not a hold
// speed, and the core's own F key is a fixed 2x that only works while the
// multiplier is 1. So the turbo key down sets the multiplier and up restores
// it, and F is dropped so there is one fast-forward key. Blur releases
// everything in case a keyup is lost to a focus change.
// `resting` is the speed to return to when the key is released or the window
// loses focus: 1, or the chosen multiplier while the fast-forward toggle is on.
export function bindKeyboardExtras(core: MgbaCore, multiplier: () => number, resting: () => number): () => void {
  const onKey = (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey || capturing) return;
    if ((e.target as Element | null)?.matches?.("input, select, textarea")) return;
    const down = e.type === "keydown";
    const button = extraKeys()[e.code];
    if (button) {
      e.preventDefault();
      if (!e.repeat) (down ? core.buttonPress : core.buttonUnpress)(button);
    } else if (e.code === "KeyF") {
      e.stopImmediatePropagation();
    } else if (e.code === turboKey()) {
      e.preventDefault();
      if (!e.repeat) core.setFastForwardMultiplier(down ? multiplier() : resting());
    }
  };
  const release = () => {
    core.setFastForwardMultiplier(resting());
    for (const button of Object.values(extraKeys())) core.buttonUnpress(button);
  };
  const rebind = () => {
    release();
    applyKeymap(core);
  };
  window.addEventListener("keydown", onKey, true);
  window.addEventListener("keyup", onKey, true);
  window.addEventListener("blur", release);
  const stop = onKeymapChange(rebind);
  return () => {
    window.removeEventListener("keydown", onKey, true);
    window.removeEventListener("keyup", onKey, true);
    window.removeEventListener("blur", release);
    stop();
  };
}
